import React, { useState, useMemo } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { 
  FiFilter, 
  FiSearch, 
  FiEdit2, 
  FiTrash2, 
  FiBox, 
  FiAlertCircle, 
  FiCheckCircle, 
  FiPlus 
} from "react-icons/fi";

function ManageSku() {
  // Sample data (Added mock data to demonstrate the UI)
  const [products] = useState([
    { id: 1, sku: "PRO-001", name: "Wireless Headphones", vendor: "TechCorp", category: "Electronics", price: 99, stock: 15, status: "Active", createdAt: "2023-10-01" },
    { id: 2, sku: "PRO-002", name: "Gaming Mouse", vendor: "LogiNext", category: "Electronics", price: 45, stock: 0, status: "Active", createdAt: "2023-10-05" },
    { id: 3, sku: "PRO-003", name: "Cotton T-Shirt", vendor: "StyleHub", category: "Apparel", price: 25, stock: 120, status: "Inactive", createdAt: "2023-10-10" },
  ]);

  const [filters, setFilters] = useState({
    search: "",
    vendor: "All",
    category: "All",
    status: "All",
    stock: "All",
    priceMin: "",
    priceMax: "",
    dateFrom: null,
    dateTo: null,
  });

  const [showFilter, setShowFilter] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleDateChange = (name, date) => {
    setFilters({ ...filters, [name]: date[0] });
  };

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch =
        p.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        p.sku.toLowerCase().includes(filters.search.toLowerCase());
      const matchesVendor = filters.vendor === "All" || p.vendor === filters.vendor;
      const matchesCategory = filters.category === "All" || p.category === filters.category;
      const matchesStatus = filters.status === "All" || p.status === filters.status;
      const matchesStock = filters.stock === "All" || (filters.stock === "InStock" && p.stock > 0) || (filters.stock === "OutOfStock" && p.stock === 0);
      const matchesPrice = (filters.priceMin === "" || p.price >= Number(filters.priceMin)) && (filters.priceMax === "" || p.price <= Number(filters.priceMax));
      
      return matchesSearch && matchesVendor && matchesCategory && matchesStatus && matchesStock && matchesPrice;
    });
  }, [products, filters]);

  const vendors = ["All", ...new Set(products.map((p) => p.vendor))];
  const categories = ["All", ...new Set(products.map((p) => p.category))];

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper" style={{ backgroundColor: "#f4f7fe", minHeight: "100vh" }}>
        <div className="main-content p-4">
          
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h3 className="fw-bold mb-1">SKU Inventory</h3>
              <p className="text-muted small mb-0">Manage your product stock levels and vendor assignments.</p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-white shadow-sm d-flex align-items-center gap-2 border" onClick={() => setShowFilter(true)}>
                <FiFilter /> Filters
              </button>
              <button className="btn btn-primary d-flex align-items-center gap-2 shadow-sm">
                <FiPlus /> Add SKU
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-primary-subtle text-primary p-3 rounded-circle"><FiBox size={24} /></div>
                  <div>
                    <h5 className="mb-0 fw-bold">{products.length}</h5>
                    <small className="text-muted">Total SKUs</small>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-danger-subtle text-danger p-3 rounded-circle"><FiAlertCircle size={24} /></div>
                  <div>
                    <h5 className="mb-0 fw-bold">{products.filter(p => p.stock === 0).length}</h5>
                    <small className="text-muted">Out of Stock</small>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-success-subtle text-success p-3 rounded-circle"><FiCheckCircle size={24} /></div>
                  <div>
                    <h5 className="mb-0 fw-bold">{products.filter(p => p.status === "Active").length}</h5>
                    <small className="text-muted">Active SKUs</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="card border-0 shadow-sm overflow-hidden">
            <div className="card-header bg-white border-0 py-3">
              <div className="position-relative w-25">
                <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <input 
                  type="text" 
                  className="form-control ps-5 bg-light border-0" 
                  placeholder="Quick search..." 
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4">Product Info</th>
                    <th>Vendor</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th className="text-end pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => (
                    <tr key={p.id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded bg-light d-flex align-items-center justify-content-center fw-bold text-primary" style={{width: "45px", height: "45px", fontSize: "12px"}}>
                            {p.sku.split('-')[1]}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{p.name}</div>
                            <small className="text-muted">{p.sku}</small>
                          </div>
                        </div>
                      </td>
                      <td><span className="text-dark fw-medium">{p.vendor}</span></td>
                      <td><span className="badge bg-light text-dark border">{p.category}</span></td>
                      <td className="fw-bold text-dark">${p.price}</td>
                      <td>
                        {p.stock > 0 ? (
                          <div className="text-success small fw-bold">
                            <span className="d-inline-block rounded-circle bg-success me-1" style={{width: "8px", height: "8px"}}></span>
                            {p.stock} in stock
                          </div>
                        ) : (
                          <div className="text-danger small fw-bold">
                             <span className="d-inline-block rounded-circle bg-danger me-1" style={{width: "8px", height: "8px"}}></span>
                            Out of stock
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge rounded-pill ${p.status === "Active" ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="text-end pe-4">
                        <div className="d-flex justify-content-end gap-2">
                          <button className="btn btn-sm btn-light text-primary"><FiEdit2 size={16} /></button>
                          <button className="btn btn-sm btn-light text-danger"><FiTrash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredProducts.length === 0 && (
              <div className="text-center py-5">
                <img src="https://illustrations.popsy.co/gray/box.svg" alt="empty" style={{width: "150px"}} className="mb-3" />
                <p className="text-muted">No SKUs found matching your criteria.</p>
              </div>
            )}
          </div>

          {/* Filter Side Panel (Off-canvas style) */}
          <div className={`filter-panel shadow-lg ${showFilter ? "open" : ""}`}>
            <div className="filter-header p-4 d-flex justify-content-between align-items-center border-bottom">
              <h5 className="mb-0 fw-bold">Detailed Filters</h5>
              <button className="btn-close" onClick={() => setShowFilter(false)}></button>
            </div>
            <div className="filter-body p-4">
              <div className="mb-4">
                <label className="form-label fw-semibold small text-uppercase text-muted">Stock Status</label>
                <div className="d-flex gap-2 flex-wrap">
                  {["All", "InStock", "OutOfStock"].map(opt => (
                    <button 
                      key={opt}
                      className={`btn btn-sm ${filters.stock === opt ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setFilters({...filters, stock: opt})}
                    >
                      {opt === "InStock" ? "In Stock" : opt === "OutOfStock" ? "Out of Stock" : "All"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label fw-medium">Category</label>
                  <select className="form-select" name="category" value={filters.category} onChange={handleFilterChange}>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label fw-medium">Vendor</label>
                  <select className="form-select" name="vendor" value={filters.vendor} onChange={handleFilterChange}>
                    {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div className="col-6 mb-3">
                  <label className="form-label fw-medium">Min Price</label>
                  <input type="number" className="form-control" name="priceMin" value={filters.priceMin} onChange={handleFilterChange} />
                </div>
                <div className="col-6 mb-3">
                  <label className="form-label fw-medium">Max Price</label>
                  <input type="number" className="form-control" name="priceMax" value={filters.priceMax} onChange={handleFilterChange} />
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label fw-medium">Created After</label>
                  <Flatpickr value={filters.dateFrom} onChange={(d) => handleDateChange("dateFrom", d)} options={{ dateFormat: "Y-m-d" }} className="form-control" />
                </div>
              </div>
              
              <div className="mt-4 pt-3 border-top d-flex gap-2">
                <button className="btn btn-primary w-100" onClick={() => setShowFilter(false)}>Apply</button>
                <button className="btn btn-light w-100" onClick={() => setFilters({
                   search: "", vendor: "All", category: "All", status: "All", stock: "All", priceMin: "", priceMax: "", dateFrom: null, dateTo: null
                })}>Reset</button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .btn-white { background: #fff; color: #333; }
        .bg-primary-subtle { background-color: #e0eaff; }
        .bg-success-subtle { background-color: #dcfce7; }
        .bg-danger-subtle { background-color: #fee2e2; }
        .bg-secondary-subtle { background-color: #f3f4f6; }
        
        .filter-panel {
          position: fixed;
          top: 0;
          right: -400px;
          width: 380px;
          height: 100vh;
          background: #fff;
          z-index: 9999;
          transition: 0.3s ease-in-out;
        }
        .filter-panel.open { right: 0; }
        
        .table thead th {
          font-weight: 600;
          font-size: 13px;
          color: #6b7280;
          text-transform: uppercase;
        }
        .form-control:focus, .form-select:focus {
          box-shadow: none;
          border-color: #4e73df;
        }
        .smaller { font-size: 11px; }
      `}</style>
    </>
  );
}

export default ManageSku;